import { toast } from "sonner";
import { READ_ONLY_MESSAGE } from "@/lib/roles";

// ---------------------------------------------------------------------------
// Phase 191 — global view-only engine for "Read Only — view all" sessions.
//
// The server refuses every write / export from a read-only account; this layer makes the UI honest about it without
// touching 70+ pages one by one. While active it:
//   1. tags action controls with `data-ro-blocked` — buttons / menu items whose label starts with an action verb
//      (Add, Edit, Delete, Approve, Send, Lodge, Upload, Share, Download, Export, Print, Save…), download / export links,
//      and file inputs — but never navigation (Open, View, Filter, Next, Close, Sign out…) or anything inside
//      `[data-ro-allow]`;
//   2. greys them out via a stylesheet rule on `[data-ro-blocked]` (see index.css);
//   3. stops clicks / Enter / Space / file picks / drops / form submits on them in the capture phase, showing one
//      throttled toast instead of an error.
// Screens that already carry an explicit `readOnly` prop simply hide their actions; this is the safety net for the rest.
// ---------------------------------------------------------------------------

const ACTION_RE = new RegExp(
  "^(" +
    [
      "add", "new", "create", "edit", "delete", "remove", "approve", "reject", "decline", "send", "resend", "lodge", "upload", "share",
      "unshare", "save", "submit", "import", "export", "download", "print", "connect", "disconnect", "sync", "resolve", "reopen",
      "assign", "unassign", "invite", "publish", "unpublish", "issue", "reissue", "record", "log", "post", "attach", "duplicate",
      "clone", "archive", "unarchive", "restore", "convert", "generate", "regenerate", "apply", "pay", "raise", "request", "schedule",
      "reschedule", "book", "move", "rename", "sign", "accept", "complete", "mark", "start", "stop", "clock", "drop pin", "add pin",
      "reply", "comment", "update", "change", "set", "enable", "disable", "activate", "deactivate", "revoke", "grant", "link", "unlink",
      "merge", "split", "allocate", "unallocate", "release", "void", "write off", "write-off", "withdraw", "finalise", "finalize",
      "close out", "supersede", "replace", "rotate", "crop", "discard", "run", "analyse", "analyze", "ask", "draft", "compose",
      "email", "sms", "notify", "remind", "nudge", "escalate", "transfer", "order", "purchase", "award", "lock", "unlock", "confirm",
      "verify", "bulk", "invite", "onboard", "terminate", "reconcile", "match", "allocate", "split", "tag", "untag", "pin", "unpin",
      "save as", "use this", "use template", "insert", "append", "paste", "clear all", "reset to", "delete all", "remove all",
      "turn on", "turn off", "switch on", "switch off", "make", "build", "compile", "plan with", "take photo", "capture",
      "report", "fix", "undo", "redo", "re-run", "rerun", "access", "manage", "configure", "customise", "customize", "install",
      "trigger", "dispatch", "queue", "retry send", "resync", "re-sync", "refund", "charge", "bill", "credit", "adjust", "override",
      "maximise canvas", // never matched — keeps the alternation terminator simple
    ]
      .map((w) => w.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&"))
      .join("|") +
    ")(\\b|…|\\.\\.\\.)",
  "i"
);

// navigation / harmless controls — checked first, always allowed
const ALLOW_RE = new RegExp(
  "^(" +
    [
      "cancel", "close", "back", "next", "previous", "prev", "open", "view", "show", "hide", "filter", "search", "refresh", "reload",
      "retry", "try again", "expand", "collapse", "fit", "zoom", "maximise", "maximize", "minimise", "minimize", "exit", "sort", "copy",
      "help", "got it", "ok", "okay", "dismiss", "skip", "more", "less", "details", "see", "preview", "toggle", "menu", "navigation",
      "sign out", "log out", "logout", "switch", "dark", "light", "theme", "clear filter", "clear filters", "clear search",
      "clear selection", "reset filter", "reset filters", "reset zoom", "reset view", "select all", "deselect", "jump", "go to",
      "scroll", "page", "first", "last", "all", "today", "week", "month", "year", "day", "list", "grid", "map", "table", "calendar",
      "timeline", "home", "dashboard", "settings", "profile", "notifications", "bell", "tab", "done", "continue", "learn more",
      "read more", "load more", "show more", "show less", "what's this", "explain", "compare", "sidebar", "pins", "viewer", "markup",
      "documents", "fullscreen", "full screen", "play", "pause", "mute", "unmute", "choose", "pick", "select", "change view",
      "change page", "change month", "change week", "previous photo", "next photo", "fit to screen", "open original",
      "pin panel", "unpin panel", "pin sidebar", "unpin sidebar", "pin the", "unpin the", "pin nav", "unpin nav", "keep panel", "collapse panel",
      "expand panel", "hide panel", "show panel", "start your day", "check the", "watch the", "look at", "browse", "explore", "tour",
      "order by", "sort by", "group by", "mark as read", "mark all read", "mark all as read", "mark read", "read all",
    ]
      .map((w) => w.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&"))
      .join("|") +
    ")(\\b|…)",
  "i"
);

const EXPORT_HREF_RE = /(\.(pdf|csv|xlsx|xls|zip|ics|docx)(\?|$))|(\/export(\/|\.|\?|$))|(\/(pdf|csv|excel)(\/|\?|$))|(-pdf(\/|\?|$))/i;

const CANDIDATE_SELECTOR = 'button, [role="menuitem"], [role="menuitemcheckbox"], [role="menuitemradio"], a[download], a[href], input[type="file"], [data-ro-block]';

let active = false;
let observer = null;
let scanQueued = false;
let lastToastAt = 0;

const labelOf = (el) => {
  // the element's own words only — never anything this engine added (that would flip the decision on the next scan)
  const aria = el.getAttribute("aria-label") || (el.getAttribute("title") !== "View-only account" ? el.getAttribute("title") : "") || "";
  const text = (el.textContent || "").replace(/\s+/g, " ").trim();
  return `${aria} ${text}`.trim().slice(0, 80);
};

// things the verb list would wrongly catch — numbered pin markers ("Pin 5: …"), photo counters, pagination
const ALLOW_PATTERNS = [/^pin #?\d/i, /^\d+ of \d+/i, /^page \d/i, /^(view|open|show) /i];

const isBlockedControl = (el) => {
  if (el.closest("[data-ro-allow]")) return false;
  if (el.hasAttribute("data-ro-block")) return true;
  const tag = el.tagName;
  if (tag === "INPUT") return el.type === "file";
  if (tag === "A") {
    if (el.hasAttribute("download")) return true;
    const href = el.getAttribute("href") || "";
    return href.startsWith("/api/") || href.includes("/api/") ? EXPORT_HREF_RE.test(href.split("?")[0]) : false;
  }
  const role = el.getAttribute("role") || "";
  if (["tab", "combobox", "checkbox", "radio", "switch", "option", "listbox"].includes(role)) return false;
  if (el.getAttribute("aria-haspopup") === "listbox") return false;
  if (el.closest('[role="tablist"], [role="listbox"], nav, [data-sonner-toaster]')) return false;
  const label = labelOf(el);
  if (!label) return false;
  if (ALLOW_PATTERNS.some((re) => re.test(label)) || ALLOW_RE.test(label)) return false;
  return ACTION_RE.test(label);
};

const applyTag = (el, blocked) => {
  if (blocked) {
    if (!el.hasAttribute("data-ro-blocked")) {
      el.setAttribute("data-ro-blocked", "1");
      el.setAttribute("aria-disabled", "true");
    }
  } else if (el.hasAttribute("data-ro-blocked")) {
    el.removeAttribute("data-ro-blocked");
    el.removeAttribute("aria-disabled");
  }
};

const scan = () => {
  scanQueued = false;
  if (!active) return;
  document.querySelectorAll(CANDIDATE_SELECTOR).forEach((el) => applyTag(el, isBlockedControl(el)));
};

const queueScan = () => {
  if (scanQueued || !active) return;
  scanQueued = true;
  requestAnimationFrame(scan);
};

const say = () => {
  const now = Date.now();
  if (now - lastToastAt < 2500) return;
  lastToastAt = now;
  toast.info(READ_ONLY_MESSAGE, { id: "read-only-mode" });
};

const blockedTarget = (e) => {
  const t = e.target instanceof Element ? e.target : null;
  if (!t) return null;
  const el = t.closest("[data-ro-blocked]") || t.closest(CANDIDATE_SELECTOR);
  if (!el) return null;
  if (!el.hasAttribute("data-ro-blocked")) {
    // freshly rendered and not yet scanned — decide on the spot
    const blocked = isBlockedControl(el);
    applyTag(el, blocked);
    if (!blocked) return null;
  }
  return el;
};

const stop = (e) => {
  e.preventDefault();
  e.stopImmediatePropagation();
  e.stopPropagation();
};

const onClick = (e) => {
  if (!active) return;
  const el = blockedTarget(e);
  if (!el) return;
  stop(e);
  say();
};

const onKey = (e) => {
  if (!active || (e.key !== "Enter" && e.key !== " ")) return;
  const el = blockedTarget(e);
  if (!el) return;
  stop(e);
  say();
};

const onChange = (e) => {
  if (!active) return;
  const t = e.target;
  if (t instanceof HTMLInputElement && t.type === "file" && !t.closest("[data-ro-allow]")) {
    t.value = "";
    stop(e);
    say();
  }
};

const onDrop = (e) => {
  if (!active) return;
  if (e.dataTransfer?.files?.length && !(e.target instanceof Element && e.target.closest("[data-ro-allow]"))) {
    stop(e);
    say();
  }
};

const onSubmit = (e) => {
  if (!active) return;
  const form = e.target;
  if (form instanceof HTMLFormElement && !form.closest("[data-ro-allow]") && !form.closest("[data-ro-search]") && !form.querySelector('input[type="search"]')) {
    stop(e);
    say();
  }
};

export const startReadOnlyMode = () => {
  if (active) return;
  active = true;
  document.documentElement.setAttribute("data-read-only", "1");
  document.addEventListener("click", onClick, true);
  document.addEventListener("keydown", onKey, true);
  document.addEventListener("change", onChange, true);
  document.addEventListener("drop", onDrop, true);
  document.addEventListener("submit", onSubmit, true);
  observer = new MutationObserver(queueScan);
  observer.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ["aria-label", "title", "href", "download"] });
  queueScan();
};

export const stopReadOnlyMode = () => {
  if (!active) return;
  active = false;
  document.documentElement.removeAttribute("data-read-only");
  document.removeEventListener("click", onClick, true);
  document.removeEventListener("keydown", onKey, true);
  document.removeEventListener("change", onChange, true);
  document.removeEventListener("drop", onDrop, true);
  document.removeEventListener("submit", onSubmit, true);
  observer?.disconnect();
  observer = null;
  document.querySelectorAll("[data-ro-blocked]").forEach((el) => applyTag(el, false));
};

export const isReadOnlyModeActive = () => active;

/** Exposed for tests / debugging — what the engine thinks of a given label. */
export const classifyLabel = (label) =>
  ALLOW_PATTERNS.some((re) => re.test(label)) || ALLOW_RE.test(label) ? "allow" : ACTION_RE.test(label) ? "block" : "neutral";
